import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import useApiHelper from "../hooks/useApiHelper";
import { useFeatureEnabled } from "../hooks/useFeatureFlag";
import { DefaultResponseWithData } from "../models/system";

// Numeric to match the API's enum serialisation (no string enum converter configured).
export enum DamageAssessmentStatus {
  Pending,
  Processing,
  Complete,
  Failed,
}

export interface IDamageAssessmentOriginalImage {
  documentId: string;
  cdnUrl: string;
}

export interface IDamageAssessmentAnnotatedImage {
  sourceEvidenceUrl: string;
  cdnUrl: string;
  damageCount: number;
}

export interface IDamageAssessmentDamageInstance {
  damageType: string | null;
  sizeCategory: string | null;
  areaProportion: number | null;
  confidence: number | null;
  locations: string[];
  annotatedImageUrls: string[];
}

export interface IDamageAssessmentPartResult {
  tractableCode: string;
  partState: string | null;
  partUsability: string | null;
  confidence: number | null;
  damageInstances: IDamageAssessmentDamageInstance[];
}

export interface IDamageAssessmentDamagedCategory {
  partCategoryId: string;
  categoryName: string;
  tractableCode: string;
}

export interface IDamageAssessment {
  id: string;
  vehicleId: string;
  status: DamageAssessmentStatus;
  created: string;
  completedOn: string | null;
  failureReason: string | null;
  isStale: boolean;
  originalImages: IDamageAssessmentOriginalImage[];
  annotatedImages: IDamageAssessmentAnnotatedImage[];
  partResults: IDamageAssessmentPartResult[];
  damagedCategories: IDamageAssessmentDamagedCategory[];
}

export interface IDamageAssessmentResponse
  extends DefaultResponseWithData<IDamageAssessment | null> {}

const isInFlight = (assessment: IDamageAssessment | null | undefined) =>
  !!assessment &&
  (assessment.status === DamageAssessmentStatus.Pending ||
    assessment.status === DamageAssessmentStatus.Processing);

export default function useDamageAssessment(vehicleId: string | undefined) {
  const { get, post } = useApiHelper();
  const queryClient = useQueryClient();
  const damageAiEnabled = useFeatureEnabled("Damage AI");

  const assessment = useQuery({
    queryKey: ["damageAssessment", vehicleId],
    queryFn: () => get<IDamageAssessmentResponse>(`/damageai/vehicle/${vehicleId}`),
    enabled: !!vehicleId && damageAiEnabled,
    // Poll while Tractable is working; stop once terminal or stale (callback likely lost).
    refetchInterval: (data) =>
      isInFlight(data?.data) && !data?.data?.isStale ? 5000 : false,
  });

  const startAssessment = useMutation(
    () => post<null, IDamageAssessmentResponse>(`/damageai/vehicle/${vehicleId}`, null),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["damageAssessment", vehicleId]);
      },
    }
  );

  return {
    damageAiEnabled,
    assessment,
    startAssessment,
  };
}
